import { ResourceType } from '@cotera/api';
import type { BreadcrumbItem } from '../resources/resource-page-shell';
import type { FolderAncestor } from './use-folder-ancestors';

/**
 * The trail down to a folder: the Library root, every ancestor between it and
 * the folder, then the folder itself.
 *
 * Shared by the folder page and by folder-scoped views that live *under* a
 * folder without being it — the folder's chat history, reached from the
 * collapsed "Chats" row. That view rendered with no trail at all, so opening a
 * folder's chats dropped you somewhere that neither named where you were nor
 * offered a way back up; passing `leaf` puts it at the end of its folder's
 * trail, with the folder itself now a link rather than the current page.
 *
 * Pure so the shape of the trail can be tested without React.
 */
export function buildFolderTrailCrumbs(params: {
  orgSlug: string;
  /**
   * The folder this trail ends at (or ends *under*, when `leaf` is set). `null`
   * for a folder that can't be read (revoked access, stale link): the trail
   * shortens to the Library root rather than vanishing and taking the way back
   * up with it.
   */
  folder: {
    id: string;
    name: string;
    scope: 'user' | 'org' | 'external' | null;
  } | null;
  /** Root-most first, excluding the folder itself — see `useFolderAncestors`. */
  ancestors: readonly FolderAncestor[];
  /**
   * Label for a view nested under the folder ("Chats"). Omit when the folder
   * *is* the page.
   */
  leaf?: string;
}): BreadcrumbItem[] {
  const { orgSlug, folder, ancestors, leaf } = params;
  const isShared = folder?.scope === 'org' || folder?.scope === 'external';
  const rootCrumb: BreadcrumbItem = {
    label: isShared ? 'Shared' : 'Library',
    href: `/${orgSlug}/folders`,
    opensLibrary: true,
  };
  // Every ancestor is clickable. The folder-contents payload only carries the
  // current folder's name, so a nested folder used to render a flat
  // `Library › Current` with no way to reach anything in between.
  const crumbFor = (target: { id: string; name: string }): BreadcrumbItem => ({
    label: target.name,
    // Folders render as artifacts, so this opens in-panel rather than tearing a
    // chat/editor host out of view. The trail's LAST crumb is drawn as plain
    // text whether or not it carries a target, so the folder page's own folder
    // crumb stays unlinked without a second code path here.
    target: {
      resourceType: ResourceType.Folder,
      resourceId: target.id,
      name: target.name,
    },
  });
  const ancestorCrumbs =
    folder === null
      ? []
      : ancestors.map((ancestor) =>
          crumbFor({ id: ancestor.id, name: ancestor.name })
        );

  return [
    rootCrumb,
    ...ancestorCrumbs,
    ...(folder === null
      ? []
      : [crumbFor({ id: folder.id, name: folder.name })]),
    ...(leaf === undefined ? [] : [{ label: leaf }]),
  ];
}
